import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api, { FilterPreferences } from '../../services/api';
import { useListLayoutPreferences } from '../../hooks/useListLayoutPreferences';
import { useFilterPreferences } from '../../hooks/useFilterPreferences';
import { useTicketEvents } from '../../hooks/useTicketEvents';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import ListLayoutSettingsModal from '../../components/ListLayoutSettingsModal';
import ListLayoutSettingsButton from '../../components/ListLayoutSettingsButton';
import RefreshButton from '../../components/RefreshButton';
import FilterDropdown, { FilterSection } from '../../components/FilterDropdown';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import TableColumnFilter from '../../components/TableColumnFilter';
import { getDurationPresetRange, DurationPreset } from '../dashboard/utils/dateHelpers';
import GenericExportButton from '../../components/GenericExportButton';
import { ExportConfigs } from '../../services/genericExportService';
import { useAuth } from '../../contexts/AuthContext';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { trackAlertOperation } from '../../utils/appInsights';
import { usePersistListFiltersSession } from '../../hooks/usePersistListFiltersSession';
import { LIST_FILTER_SESSION_KEYS } from '../../constants/listFilterSessionKeys';
import { isTicketSearchInputValid } from '../../utils/searchTerms';
import {
  MagnifyingGlassIcon,
  EyeIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

interface Alert {
  id?: string;
  _id?: string;
  number: string;
  shortDescription: string;
  description?: string;
  priority: number;
  state: string;
  severity: string;
  source: string;
  node?: string;
  metric?: string;
  category?: string;
  type?: string;
  sourceService?: string;
  sourceSubService?: string;
  title?: string;
  status?: string;
  serviceType?: string;
  acknowledged: boolean;
  acknowledgedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  acknowledgedAt?: string;
  firstSeen: string;
  lastSeen: string;
  count: number;
  Customer?: {
    id: string;
    accountName: string;
    accountShortName?: string;
  };
  Engagement?: {
    id: string;
    name: string;
    status: string;
  };
  Resource?: {
    id: string;
    name: string;
    resourceType: string;
  };
  relatedCaseId?: string;
  createdAt: string;
  updatedAt: string;
}



const stateColors = {
  new: 'bg-blue-100 text-blue-800',
  open: 'bg-blue-100 text-blue-800',
  acknowledged: 'bg-yellow-100 text-yellow-800',
  assigned: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  resolved: 'bg-green-100 text-green-800',
  auto_resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
  suppressed: 'bg-orange-100 text-orange-800'
};

const stateLabels = {
  new: 'New',
  open: 'Open',
  acknowledged: 'Acknowledged',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  auto_resolved: 'Auto-Resolved',
  closed: 'Closed',
  suppressed: 'Suppressed'
};

const categoryLabels = {
  problem_incident: 'Problem/Incident',
  labtech: 'LabTech'
};

const severityLabels = {
  critical: 'Critical',
  major: 'Major',
  minor: 'Minor',
  warning: 'Warning',
  info: 'Info'
};

// Options for multi-select filters
const stateOptions = [
  { value: 'new', label: 'New' },
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'auto_resolved', label: 'Auto-Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'suppressed', label: 'Suppressed' },
];

/** Default list view: active alerts only (exclude terminal states). */
const DEFAULT_ALERT_OPEN_STATES = [
  'new',
  'open',
  'acknowledged',
  'assigned',
  'in_progress',
] as const;

const DEFAULT_ALERT_OPEN_STATES_SORTED = [...DEFAULT_ALERT_OPEN_STATES].sort().join(',');

function areDefaultAlertOpenStates(states: string[]): boolean {
  if (states.length !== DEFAULT_ALERT_OPEN_STATES.length) return false;
  return [...states].sort().join(',') === DEFAULT_ALERT_OPEN_STATES_SORTED;
}

/** Serialize state multi-select for URL: omit default open set; use `all` for no filter. */
function serializeAlertStatesForUrl(states: string[]): string | null {
  if (states.length === 0) return 'all';
  if (areDefaultAlertOpenStates(states)) return null;
  return states.join(',');
}

const severityOptions = [
  { value: 'critical', label: 'Critical' },
  { value: 'major', label: 'Major' },
  { value: 'minor', label: 'Minor' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

const timeRangeOptions = [
  { value: 'preset:today', label: 'Today' },
  { value: 'preset:yesterday', label: 'Yesterday' },
  { value: 'preset:this_week', label: 'This Week' },
  { value: 'preset:last_week', label: 'Last Week' },
  { value: 'preset:this_month', label: 'This Month' },
  { value: 'preset:last_month', label: 'Last Month' },
  { value: '60', label: 'Last 1 hour' },
  { value: '120', label: 'Last 2 hours' },
  { value: '480', label: 'Last 8 hours' },
  { value: '1440', label: 'Last 24 hours' },
];

// Helper function to strip HTML tags and decode HTML entities
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Helper function to convert preset time ranges to custom format for API
const convertPresetToCustom = (timeRangeValue: string): string => {
  if (!timeRangeValue.startsWith('preset:')) {
    return timeRangeValue; // Return as-is if not a preset
  }

  const preset = timeRangeValue.replace('preset:', '') as DurationPreset;
  const range = getDurationPresetRange(preset);

  if (range.from && range.to) {
    return `custom:${range.from},${range.to}`;
  }

  return timeRangeValue;
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const categoryOptions = [
  { value: 'problem_incident', label: 'Problem/Incident' },
  { value: 'labtech', label: 'LabTech' },
];

const ALERT_LIST_URL_FILTER_KEYS = [
  'search',
  'number',
  'desc',
  'state',
  'severity',
  'time',
  'customer',
  'engagement',
  'caseNumber',
  'acknowledged',
  'source',
  'category',
  'alertType',
  'acknowledgedBy',
  'resource',
  'firstSeen',
  'lastSeen',
  'acknowledgedAt',
] as const;

function isDefaultAlertListUrl(sp: URLSearchParams): boolean {
  const page = sp.get('page');
  if (page && page !== '1') return false;
  const ps = sp.get('pageSize');
  if (ps && ps !== '25') return false;
  for (const k of ALERT_LIST_URL_FILTER_KEYS) {
    const v = sp.get(k);
    if (v !== null && v !== '') return false;
  }
  return true;
}

function buildAlertListUrlUpdatesFromSaved(
  savedFilters: FilterPreferences
): Record<string, string | null | undefined> {
  const updates: Record<string, string | null | undefined> = {};
  if (Array.isArray(savedFilters.state)) {
    if (savedFilters.state.length === 0) {
      updates.state = 'all';
    } else if (!areDefaultAlertOpenStates(savedFilters.state)) {
      updates.state = savedFilters.state.join(',');
    }
  }
  if (savedFilters.severity?.length) updates.severity = savedFilters.severity.join(',');
  // Implicit default is This Month — only write time when saved preference differs
  if (savedFilters.timeRange === '') {
    updates.time = 'all';
  } else if (savedFilters.timeRange && savedFilters.timeRange !== 'preset:this_month') {
    updates.time = savedFilters.timeRange;
  }
  if (savedFilters.customer) updates.customer = savedFilters.customer;
  if (savedFilters.engagement) {
    updates.engagement = savedFilters.engagement;
  }
  if (savedFilters.acknowledged) updates.acknowledged = savedFilters.acknowledged;
  return updates;
}

function serializeAlertTimeRangeForUrl(timeRange: string): string | null {
  if (!timeRange) return 'all';
  if (timeRange === 'preset:this_month') return null;
  return timeRange;
}

function parseOptionalAlertTimeFilter(sp: URLSearchParams, key: string): string {
  const t = sp.get(key);
  if (!t || t === 'all') return '';
  return t;
}

function appendAlertDateFilterParams(
  params: URLSearchParams,
  prefix: string,
  rangeValue: string
): void {
  if (!rangeValue) return;
  const converted = convertPresetToCustom(rangeValue);
  if (converted.startsWith('custom:')) {
    const [, dates] = converted.split('custom:');
    const [from, to] = dates.split(',');
    if (from) params.append(`${prefix}From`, from);
    if (to) params.append(`${prefix}To`, to);
  } else {
    params.append(`${prefix}Range`, converted);
  }
}

function buildAlertDateFilterExportFields(
  prefix: string,
  rangeValue: string
): Record<string, string> {
  if (!rangeValue) return {};
  const converted = convertPresetToCustom(rangeValue);
  if (converted.startsWith('custom:')) {
    const [, dates] = converted.split('custom:');
    const [from, to] = dates.split(',');
    return {
      ...(from ? { [`${prefix}From`]: from } : {}),
      ...(to ? { [`${prefix}To`]: to } : {}),
    };
  }
  if (rangeValue.startsWith('preset:')) {
    const presetName = rangeValue.replace('preset:', '') as DurationPreset;
    const range = getDurationPresetRange(presetName);
    return {
      ...(range.from ? { [`${prefix}From`]: range.from } : {}),
      ...(range.to ? { [`${prefix}To`]: range.to } : {}),
    };
  }
  return { [`${prefix}Range`]: converted };
}

const AlertList: React.FC = () => {
  // Subscribe to real-time alert events via SSE
  useTicketEvents({ ticketTypes: ['ALERT'] });

  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const hasLoggedView = useRef(false);
  const { formatDate, formatTime } = useDateFormatter();

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  usePersistListFiltersSession('/alerts', LIST_FILTER_SESSION_KEYS.alerts);

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const appliedSearchTerm = searchParams.get('search') ?? '';
  const filterNumber = searchParams.get('number') ?? '';
  const filterDescription = searchParams.get('desc') ?? '';
  const effectiveSearch = appliedSearchTerm || filterNumber || filterDescription;
  useEffect(() => {
    setSearchInput(appliedSearchTerm);
  }, [appliedSearchTerm]);

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '25', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 25;
  }, [searchParams]);

  const filterState = useMemo(() => {
    const t = searchParams.get('state');
    // Explicit all states (no state filter on API)
    if (t === 'all') return [] as string[];
    // Default: exclude Resolved / Auto-Resolved / Closed / Suppressed
    if (t === null || t === '') return [...DEFAULT_ALERT_OPEN_STATES];
    return t.split(',').filter(Boolean);
  }, [searchParams]);
  const filterSeverity = useMemo(
    () => (searchParams.get('severity') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterTimeRange = useMemo(() => {
    const t = searchParams.get('time');
    if (t === 'all') return '';
    // Default to this month for faster list loads
    if (t === null || t === '') return 'preset:this_month';
    return t;
  }, [searchParams]);

  const filterCustomer = searchParams.get('customer') ?? '';
  const filterEngagement = useMemo(
    () => (searchParams.get('engagement') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const appliedCaseNumber = searchParams.get('caseNumber') ?? '';
  const [filterCaseNumber, setFilterCaseNumber] = useState(() => searchParams.get('caseNumber') ?? '');
  useEffect(() => {
    setFilterCaseNumber(appliedCaseNumber);
    filterCaseNumberRef.current = appliedCaseNumber;
  }, [appliedCaseNumber]);

  const filterAcknowledged = searchParams.get('acknowledged') ?? '';
  const filterSource = searchParams.get('source') ?? '';
  const filterCategory = searchParams.get('category') ?? '';
  const filterAlertType = searchParams.get('alertType') ?? '';
  const filterAcknowledgedBy = searchParams.get('acknowledgedBy') ?? '';
  const filterResource = searchParams.get('resource') ?? '';
  const filterFirstSeen = useMemo(
    () => parseOptionalAlertTimeFilter(searchParams, 'firstSeen'),
    [searchParams]
  );
  const filterLastSeen = useMemo(
    () => parseOptionalAlertTimeFilter(searchParams, 'lastSeen'),
    [searchParams]
  );
  const filterAcknowledgedAt = useMemo(
    () => parseOptionalAlertTimeFilter(searchParams, 'acknowledgedAt'),
    [searchParams]
  );

  const filterCaseNumberRef = useRef('');

  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const savedPrefsHydratedRef = useRef(false);

  // Filter preferences
  const {
    filters: savedFilters,
    saveFilters,
    isSaving: isFilterSaving,
    isFetched: isFilterPrefsFetched,
  } = useFilterPreferences('ALERT');

  // Apply saved preferences to URL once when URL is still default (no explicit filters)
  useEffect(() => {
    if (filtersInitialized) return;
    if (user?.id && !isFilterPrefsFetched) return;

    if (!isDefaultAlertListUrl(searchParams)) {
      setFiltersInitialized(true);
      return;
    }

    if (savedPrefsHydratedRef.current) {
      setFiltersInitialized(true);
      return;
    }
    savedPrefsHydratedRef.current = true;

    const updates = buildAlertListUrlUpdatesFromSaved(savedFilters);
    if (Object.keys(updates).length > 0) {
      patchSearchParams(updates);
    }
    setFiltersInitialized(true);
  }, [
    filtersInitialized,
    isFilterPrefsFetched,
    user?.id,
    searchParams,
    savedFilters,
    patchSearchParams,
  ]);

  // Check if current filters differ from saved
  const savedEngagement = savedFilters.engagement
    ? (typeof savedFilters.engagement === 'string' ? savedFilters.engagement.split(',') : savedFilters.engagement)
    : [];
  const hasUnsavedFilterChanges = filtersInitialized && (
    JSON.stringify([...filterState].sort()) !==
      JSON.stringify(
        (Array.isArray(savedFilters.state)
          ? savedFilters.state
          : [...DEFAULT_ALERT_OPEN_STATES]
        ).slice().sort()
      ) ||
    JSON.stringify(filterSeverity.sort()) !== JSON.stringify((savedFilters.severity || []).sort()) ||
    filterTimeRange !== (savedFilters.timeRange || 'preset:this_month') ||
    filterCustomer !== (savedFilters.customer || '') ||
    JSON.stringify([...filterEngagement].sort()) !== JSON.stringify([...savedEngagement].sort()) ||
    filterAcknowledged !== (savedFilters.acknowledged || '')
  );

  const handleSaveFilters = async () => {
    await saveFilters({
      state: filterState,
      severity: filterSeverity,
      timeRange: filterTimeRange || undefined,
      customer: filterCustomer || undefined,
      engagement: filterEngagement.length > 0 ? filterEngagement.join(',') : undefined,
      acknowledged: filterAcknowledged || undefined,
    });
  };

  // List layout settings
  const [showLayoutSettings, setShowLayoutSettings] = useState(false);
  const {
    columns: layoutColumns,
    visibleColumns,
    saveColumns,
    resetToDefault,
    isSaving: isLayoutSaving,
    isResetting: isLayoutResetting,
  } = useListLayoutPreferences('ALERT');

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers-dropdown'],
    queryFn: async () => {
      const response = await api.get('/customers/dropdown');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch engagements for selected customer (use UI state for immediate dropdown update)
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements', filterCustomer],
    queryFn: async () => {
      if (!filterCustomer) return { engagements: [] };
      const response = await api.get(`/engagements/dropdown?customerId=${filterCustomer}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!filterCustomer
  });

  const { data: usersDropdownData } = useQuery({
    queryKey: ['users-dropdown'],
    queryFn: async () => {
      const response = await api.get('/users/dropdown');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const customerOptions = useMemo(
    () =>
      customersData?.customers?.map((c: { id: string; accountName?: string; name?: string }) => ({
        value: c.id,
        label: c.accountName || c.name || 'Unknown',
      })) ?? [],
    [customersData]
  );

  const engagementOptions = useMemo(
    () =>
      engagementsData?.engagements?.map((e: { id: string; name: string }) => ({
        value: e.id,
        label: e.name,
      })) ?? [],
    [engagementsData]
  );

  const userFilterOptions = useMemo(
    () =>
      usersDropdownData?.users?.map((u: { id: string; firstName?: string; lastName?: string; email?: string }) => ({
        value: u.id,
        label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Unknown',
      })) ?? [],
    [usersDropdownData]
  );

  const appendListFilterParams = useCallback((params: URLSearchParams) => {
    if (effectiveSearch) params.append('search', effectiveSearch);
    if (filterState.length > 0) params.append('state', filterState.join(','));
    if (filterSeverity.length > 0) params.append('severity', filterSeverity.join(','));
    if (filterTimeRange) params.append('timeRange', convertPresetToCustom(filterTimeRange));
    if (filterCustomer) params.append('customerId', filterCustomer);
    if (filterEngagement.length > 0) params.append('engagementId', filterEngagement.join(','));
    if (appliedCaseNumber) params.append('caseNumber', appliedCaseNumber);
    if (filterAcknowledged) params.append('acknowledged', filterAcknowledged);
    if (filterSource) params.append('source', filterSource);
    if (filterCategory) params.append('category', filterCategory);
    if (filterAlertType) params.append('type', filterAlertType);
    if (filterAcknowledgedBy) params.append('acknowledgedById', filterAcknowledgedBy);
    if (filterResource) params.append('resource', filterResource);
    appendAlertDateFilterParams(params, 'firstSeen', filterFirstSeen);
    appendAlertDateFilterParams(params, 'lastSeen', filterLastSeen);
    appendAlertDateFilterParams(params, 'acknowledgedAt', filterAcknowledgedAt);
  }, [
    effectiveSearch,
    filterState,
    filterSeverity,
    filterTimeRange,
    filterCustomer,
    filterEngagement,
    appliedCaseNumber,
    filterAcknowledged,
    filterSource,
    filterCategory,
    filterAlertType,
    filterAcknowledgedBy,
    filterResource,
    filterFirstSeen,
    filterLastSeen,
    filterAcknowledgedAt,
  ]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['alerts', page, pageSize, effectiveSearch, filterState, filterSeverity, filterTimeRange, filterCustomer, filterEngagement, appliedCaseNumber, filterAcknowledged, filterSource, filterCategory, filterAlertType, filterAcknowledgedBy, filterResource, filterFirstSeen, filterLastSeen, filterAcknowledgedAt],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
      });

      appendListFilterParams(params);

      const response = await api.get(`/alerts?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000 // Keep in cache for 5 minutes
  });

  // Log "Alert List Viewed" when data is loaded successfully
  useEffect(() => {
    if (data && user && !hasLoggedView.current && !isLoading) {
      hasLoggedView.current = true;
      trackAlertOperation(
        'Alert List',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Alert List Viewed',
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
    }
  }, [data, user, isLoading, isServiceDeskUser]);

  // Fetch alert stats for the summary bar (with same filters as list)
  const { data: statsData } = useQuery({
    queryKey: ['alert-stats', effectiveSearch, filterState, filterSeverity, filterTimeRange, filterCustomer, filterEngagement, appliedCaseNumber, filterAcknowledged, filterSource, filterCategory, filterAlertType, filterAcknowledgedBy, filterResource, filterFirstSeen, filterLastSeen, filterAcknowledgedAt],
    queryFn: async () => {
      const params = new URLSearchParams();

      appendListFilterParams(params);

      const response = await api.get(`/alerts/stats?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 30000, // 30 seconds - match list query
    enabled: !isLoading,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({
      search: searchInput.trim() || null,
      page: '1',
    });
  };

  // Build filter sections for FilterDropdown
  const filterSections: FilterSection[] = useMemo(() => [
    {
      id: 'state',
      label: 'State',
      type: 'multi-select',
      options: stateOptions,
      placeholder: 'All States',
    },
    {
      id: 'severity',
      label: 'Severity',
      type: 'multi-select',
      options: severityOptions,
      placeholder: 'All Severities',
    },
    {
      id: 'timeRange',
      label: 'Time Range',
      type: 'time-range',
      options: timeRangeOptions,
      placeholder: 'All Time',
    },
    {
      id: 'customer',
      label: 'Customer',
      type: 'searchable-select',
      options: customersData?.customers?.map((c: any) => ({
        value: c.id,
        label: c.accountName || c.name,
      })) || [],
      placeholder: 'All Customers',
    },
    {
      id: 'engagement',
      label: 'Engagement',
      type: 'searchable-multi-select',
      options: engagementsData?.engagements?.map((e: any) => ({
        value: e.id,
        label: e.name,
      })) || [],
      placeholder: 'All Engagements',
      disabled: !filterCustomer,
      disabledMessage: 'Select a customer first',
    },
    {
      id: 'acknowledged',
      label: 'Acknowledged',
      type: 'single-select',
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
      placeholder: 'All',
    },
    {
      id: 'caseNumber',
      label: 'Case Number',
      type: 'text',
      placeholder: 'e.g., TKT0001234',
      helpText: 'Enter single or multiple case numbers separated by commas',
    },
  ], [customersData, engagementsData, filterCustomer]);

  const formatTimeFilterChipLabel = useCallback((rangeValue: string): string => {
    if (!rangeValue) return '';
    if (rangeValue.startsWith('preset:')) {
      const timeOption = timeRangeOptions.find((t) => t.value === rangeValue);
      return timeOption?.label || rangeValue;
    }
    const timeOption = timeRangeOptions.find((t) => t.value === rangeValue);
    if (timeOption) return timeOption.label;
    if (rangeValue.startsWith('custom:')) {
      const [, dates] = rangeValue.split('custom:');
      const [from, to] = dates.split(',');
      if (from && to) {
        try {
          const formatDateForDisplay = (dateStr: string) => {
            const date = new Date(dateStr.trim());
            if (isNaN(date.getTime())) return dateStr;
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const year = date.getFullYear();
            let hours = date.getHours();
            const minutes = date.getMinutes();
            const seconds = date.getSeconds();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
            const secondsStr = seconds < 10 ? `0${seconds}` : seconds;
            return `${month}/${day}/${year} ${hours}:${minutesStr}:${secondsStr} ${ampm}`;
          };
          return `${formatDateForDisplay(from)} - ${formatDateForDisplay(to)}`;
        } catch {
          return rangeValue;
        }
      }
    }
    return rangeValue;
  }, []);

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'number':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterNumber}
              placeholder="Filter by number (comma or space separated)..."
              onChange={(v) => {
                patchSearchParams({ number: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'shortDescription':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterDescription}
              placeholder="Filter by description..."
              onChange={(v) => {
                patchSearchParams({ desc: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'severity':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={severityOptions}
              value={filterSeverity}
              placeholder="All Severities"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ severity: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'state':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={stateOptions}
              value={filterState}
              placeholder="All States"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({
                  state: serializeAlertStatesForUrl(arr),
                  page: '1',
                });
              }}
            />
          );
        case 'source':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterSource}
              placeholder="Filter by source..."
              onChange={(v) => {
                patchSearchParams({ source: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'customer':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={customerOptions}
              value={filterCustomer}
              placeholder="All Customers"
              onChange={(v) => {
                patchSearchParams({
                  customer: typeof v === 'string' && v ? v : null,
                  engagement: null,
                  page: '1',
                });
              }}
            />
          );
        case 'engagement':
          if (!filterCustomer) return columnLabel;
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={engagementOptions}
              value={filterEngagement}
              placeholder="All Engagements"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ engagement: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'resource':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterResource}
              placeholder="Filter by resource..."
              onChange={(v) => {
                patchSearchParams({ resource: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'category':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={categoryOptions}
              value={filterCategory}
              placeholder="All Categories"
              onChange={(v) => {
                patchSearchParams({ category: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'alertType':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterAlertType}
              placeholder="Filter by type..."
              onChange={(v) => {
                patchSearchParams({ alertType: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'acknowledged':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
              value={filterAcknowledged}
              placeholder="All"
              onChange={(v) => {
                patchSearchParams({
                  acknowledged: v === 'true' || v === 'false' ? (v as string) : null,
                  page: '1',
                });
              }}
            />
          );
        case 'acknowledgedBy':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={userFilterOptions}
              value={filterAcknowledgedBy}
              placeholder="All Users"
              onChange={(v) => {
                patchSearchParams({ acknowledgedBy: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'acknowledgedAt':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={timeRangeOptions}
              value={filterAcknowledgedAt || 'all'}
              placeholder="All Time"
              active={searchParams.has('acknowledgedAt')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                patchSearchParams({
                  acknowledgedAt: val && val !== 'all' ? val : null,
                  page: '1',
                });
              }}
            />
          );
        case 'firstSeen':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={timeRangeOptions}
              value={filterFirstSeen || 'all'}
              placeholder="All Time"
              active={searchParams.has('firstSeen')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                patchSearchParams({
                  firstSeen: val && val !== 'all' ? val : null,
                  page: '1',
                });
              }}
            />
          );
        case 'lastSeen':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={timeRangeOptions}
              value={filterLastSeen || 'all'}
              placeholder="All Time"
              active={searchParams.has('lastSeen')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                patchSearchParams({
                  lastSeen: val && val !== 'all' ? val : null,
                  page: '1',
                });
              }}
            />
          );
        case 'createdAt':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={timeRangeOptions}
              value={filterTimeRange || 'all'}
              placeholder="All Time"
              active={searchParams.has('time')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                const timeParam = serializeAlertTimeRangeForUrl(val === 'all' ? '' : val);
                patchSearchParams({
                  time: timeParam,
                  page: '1',
                });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterNumber,
      filterDescription,
      filterSeverity,
      filterState,
      filterSource,
      customerOptions,
      filterCustomer,
      engagementOptions,
      filterEngagement,
      filterResource,
      filterCategory,
      filterAlertType,
      filterAcknowledged,
      userFilterOptions,
      filterAcknowledgedBy,
      filterAcknowledgedAt,
      filterFirstSeen,
      filterLastSeen,
      filterTimeRange,
      searchParams,
      patchSearchParams,
    ]
  );

  // Build active filter chips
  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (appliedSearchTerm) {
      chips.push({
        id: 'search',
        label: 'Search',
        value: `"${appliedSearchTerm}"`,
        color: 'blue',
      });
    }

    if (filterNumber) {
      chips.push({ id: 'number', label: 'Number', value: filterNumber, color: 'blue' });
    }

    if (filterDescription) {
      chips.push({ id: 'desc', label: 'Description', value: filterDescription, color: 'blue' });
    }

    if (filterState.length > 0) {
      chips.push({
        id: 'state',
        label: 'State',
        value: filterState.map(s => stateLabels[s as keyof typeof stateLabels] || s).join(', '),
        color: 'purple',
      });
    }

    if (filterSeverity.length > 0) {
      chips.push({
        id: 'severity',
        label: 'Severity',
        value: filterSeverity.map(s => severityLabels[s as keyof typeof severityLabels] || s).join(', '),
        color: 'red',
      });
    }

    if (filterTimeRange) {
      chips.push({
        id: 'timeRange',
        label: 'Created',
        value: formatTimeFilterChipLabel(filterTimeRange),
        color: 'orange',
      });
    }

    if (filterSource) {
      chips.push({ id: 'source', label: 'Source', value: filterSource, color: 'gray' });
    }

    if (filterCategory) {
      const categoryLabel = categoryLabels[filterCategory as keyof typeof categoryLabels] || filterCategory;
      chips.push({ id: 'category', label: 'Category', value: categoryLabel, color: 'purple' });
    }

    if (filterAlertType) {
      chips.push({ id: 'alertType', label: 'Alert Type', value: filterAlertType, color: 'purple' });
    }

    if (filterResource) {
      chips.push({ id: 'resource', label: 'Resource', value: filterResource, color: 'gray' });
    }

    if (filterAcknowledgedBy) {
      const user = userFilterOptions.find((u: { value: string; label: string }) => u.value === filterAcknowledgedBy);
      chips.push({
        id: 'acknowledgedBy',
        label: 'Acknowledged By',
        value: user?.label || 'Unknown',
        color: 'purple',
      });
    }

    if (filterFirstSeen) {
      chips.push({
        id: 'firstSeen',
        label: 'First Seen',
        value: formatTimeFilterChipLabel(filterFirstSeen),
        color: 'orange',
      });
    }

    if (filterLastSeen) {
      chips.push({
        id: 'lastSeen',
        label: 'Last Seen',
        value: formatTimeFilterChipLabel(filterLastSeen),
        color: 'orange',
      });
    }

    if (filterAcknowledgedAt) {
      chips.push({
        id: 'acknowledgedAt',
        label: 'Acknowledged At',
        value: formatTimeFilterChipLabel(filterAcknowledgedAt),
        color: 'orange',
      });
    }

    if (filterCustomer) {
      const customer = customersData?.customers?.find((c: any) => c.id === filterCustomer);
      chips.push({
        id: 'customer',
        label: 'Customer',
        value: customer?.accountName || customer?.name || 'Unknown',
        color: 'teal',
      });
    }

    if (filterEngagement.length > 0) {
      const engagementNames = filterEngagement.map(engId => {
        const engagement = engagementsData?.engagements?.find((e: any) => e.id === engId);
        return engagement?.name || 'Unknown';
      });
      chips.push({
        id: 'engagement',
        label: 'Engagement',
        value: engagementNames.join(', '),
        color: 'cyan',
      });
    }

    if (filterAcknowledged) {
      chips.push({
        id: 'acknowledged',
        label: 'Acknowledged',
        value: filterAcknowledged === 'true' ? 'Yes' : 'No',
        color: filterAcknowledged === 'true' ? 'green' : 'gray',
      });
    }

    if (appliedCaseNumber) {
      chips.push({
        id: 'caseNumber',
        label: 'Case Number',
        value: appliedCaseNumber,
        color: 'green',
      });
    }

    return chips;
  }, [appliedSearchTerm, filterNumber, filterDescription, filterState, filterSeverity, filterTimeRange, filterSource, filterCategory, filterAlertType, filterResource, filterAcknowledgedBy, filterFirstSeen, filterLastSeen, filterAcknowledgedAt, filterCustomer, filterEngagement, filterAcknowledged, appliedCaseNumber, customersData, engagementsData, userFilterOptions, formatTimeFilterChipLabel]);

  // Handle filter dropdown changes
  const handleFilterChange = (sectionId: string, value: string | string[]) => {
    switch (sectionId) {
      case 'state': {
        const arr = value as string[];
        patchSearchParams({
          state: serializeAlertStatesForUrl(arr),
          page: '1',
        });
        break;
      }
      case 'severity': {
        const arr = value as string[];
        patchSearchParams({
          severity: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'timeRange': {
        const v = value as string;
        const timeParam = serializeAlertTimeRangeForUrl(v);
        patchSearchParams({
          time: timeParam,
          page: '1',
        });
        break;
      }
      case 'customer':
        patchSearchParams({
          customer: (value as string) || null,
          engagement: null,
          page: '1',
        });
        break;
      case 'engagement': {
        const arr = value as string[];
        patchSearchParams({
          engagement: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'acknowledged':
        patchSearchParams({
          acknowledged:
            value === 'true' || value === 'false' ? (value as string) : null,
          page: '1',
        });
        break;
      case 'caseNumber': {
        const caseNumberValue = value as string;
        setFilterCaseNumber(caseNumberValue);
        filterCaseNumberRef.current = caseNumberValue;
        break;
      }
    }
  };

  // Handle applying case number filter
  const handleApplyFilters = (sectionId?: string, value?: string | string[]) => {
    if (sectionId !== 'caseNumber') {
      return;
    }
    const caseNumberValue =
      value !== undefined
        ? String(value).trim()
        : filterCaseNumberRef.current.trim();
    patchSearchParams({
      caseNumber: caseNumberValue || null,
      page: '1',
    });
  };

  // Handle removing a single filter chip
  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'number':
        patchSearchParams({ number: null, page: '1' });
        break;
      case 'desc':
        patchSearchParams({ desc: null, page: '1' });
        break;
      case 'state':
        patchSearchParams({ state: 'all', page: '1' });
        break;
      case 'severity':
        patchSearchParams({ severity: null, page: '1' });
        break;
      case 'timeRange':
        patchSearchParams({ time: 'all', page: '1' });
        break;
      case 'customer':
        patchSearchParams({ customer: null, engagement: null, page: '1' });
        break;
      case 'engagement':
        patchSearchParams({ engagement: null, page: '1' });
        break;
      case 'acknowledged':
        patchSearchParams({ acknowledged: null, page: '1' });
        break;
      case 'caseNumber':
        setFilterCaseNumber('');
        filterCaseNumberRef.current = '';
        patchSearchParams({ caseNumber: null, page: '1' });
        break;
      case 'source':
        patchSearchParams({ source: null, page: '1' });
        break;
      case 'category':
        patchSearchParams({ category: null, page: '1' });
        break;
      case 'alertType':
        patchSearchParams({ alertType: null, page: '1' });
        break;
      case 'resource':
        patchSearchParams({ resource: null, page: '1' });
        break;
      case 'acknowledgedBy':
        patchSearchParams({ acknowledgedBy: null, page: '1' });
        break;
      case 'firstSeen':
        patchSearchParams({ firstSeen: null, page: '1' });
        break;
      case 'lastSeen':
        patchSearchParams({ lastSeen: null, page: '1' });
        break;
      case 'acknowledgedAt':
        patchSearchParams({ acknowledgedAt: null, page: '1' });
        break;
    }
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setSearchInput('');
    setFilterCaseNumber('');
    filterCaseNumberRef.current = '';
    patchSearchParams({
      search: null,
      number: null,
      desc: null,
      state: 'all',
      severity: null,
      time: 'all',
      customer: null,
      engagement: null,
      caseNumber: null,
      acknowledged: null,
      source: null,
      category: null,
      alertType: null,
      resource: null,
      acknowledgedBy: null,
      firstSeen: null,
      lastSeen: null,
      acknowledgedAt: null,
      page: '1',
    });
  };

  // Build export filters for background export
  const exportFilters = useMemo(
    () => ({
      ...(effectiveSearch ? { search: effectiveSearch } : {}),
      ...(filterState.length > 0 ? { state: filterState.join(',') } : {}),
      ...(filterSeverity.length > 0 ? { severity: filterSeverity.join(',') } : {}),
      ...(filterTimeRange ? { timeRange: convertPresetToCustom(filterTimeRange) } : {}),
      ...(filterCustomer ? { customerId: filterCustomer } : {}),
      ...(filterEngagement.length > 0 ? { engagementId: filterEngagement.join(',') } : {}),
      ...(appliedCaseNumber ? { caseNumber: appliedCaseNumber } : {}),
      ...(filterAcknowledged ? { acknowledged: filterAcknowledged } : {}),
      ...(filterSource ? { source: filterSource } : {}),
      ...(filterCategory ? { category: filterCategory } : {}),
      ...(filterAlertType ? { type: filterAlertType } : {}),
      ...(filterAcknowledgedBy ? { acknowledgedById: filterAcknowledgedBy } : {}),
      ...(filterResource ? { resource: filterResource } : {}),
      ...buildAlertDateFilterExportFields('firstSeen', filterFirstSeen),
      ...buildAlertDateFilterExportFields('lastSeen', filterLastSeen),
      ...buildAlertDateFilterExportFields('acknowledgedAt', filterAcknowledgedAt),
    }),
    [
      effectiveSearch,
      filterState,
      filterSeverity,
      filterTimeRange,
      filterCustomer,
      filterEngagement,
      appliedCaseNumber,
      filterAcknowledged,
      filterSource,
      filterCategory,
      filterAlertType,
      filterAcknowledgedBy,
      filterResource,
      filterFirstSeen,
      filterLastSeen,
      filterAcknowledgedAt,
    ]
  );

  // Build columns array from visible columns for export
  const exportColumns = useMemo(() => {
    return visibleColumns.map(col => ({
      key: col.key,
      header: col.label || col.key,
    }));
  }, [visibleColumns]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading alerts. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Compact Header Bar */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <form onSubmit={handleSearch} className="flex items-center justify-between gap-4">
            {/* Left side: Title and Filter dropdown */}
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-bold text-gray-900 whitespace-nowrap">Alerts</h1>
              <FilterDropdown
                sections={filterSections}
                values={{
                  state: filterState,
                  severity: filterSeverity,
                  timeRange: filterTimeRange,
                  customer: filterCustomer,
                  engagement: filterEngagement,
                  acknowledged: filterAcknowledged,
                  caseNumber: filterCaseNumber,
                }}
                onChange={handleFilterChange}
                onApply={handleApplyFilters}
              />
            </div>

            {/* Right side: Search and Settings */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search alerts or numbers (comma/space separated)..."
                  className="pl-9 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                disabled={!isTicketSearchInputValid(searchInput)}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Search
              </button>
              <ListLayoutSettingsButton onClick={() => setShowLayoutSettings(true)} />
              <RefreshButton
                queryKeys={['alerts']}
                tooltip="Refresh alerts"
                size="md"
                isLoading={isLoading}
              />
            </div>
          </form>
        </div>
      </div>

      {/* Stats Summary Bar */}
      <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5">
              <span className="text-gray-600">Total:</span>
              <span className="font-semibold text-gray-900">{statsData?.total ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              <span className="text-gray-600">Critical:</span>
              <span className="font-semibold text-gray-900">{statsData?.critical ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-orange-500"></span>
              <span className="text-gray-600">Major:</span>
              <span className="font-semibold text-gray-900">{statsData?.major ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
              <span className="text-gray-600">Minor:</span>
              <span className="font-semibold text-gray-900">{statsData?.minor ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span className="text-gray-600">Acknowledged:</span>
              <span className="font-semibold text-gray-900">{statsData?.acknowledged ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              <span className="text-gray-600">Active:</span>
              <span className="font-semibold text-gray-900">{statsData?.active ?? '-'}</span>
            </span>
          </div>
          {/* Export CSV Button */}
          {data?.alerts && data.alerts.length > 0 && (
            <GenericExportButton
              config={ExportConfigs.ALERTS}
              filters={exportFilters}
              columns={exportColumns}
              totalRows={data?.total}
              disabled={!data?.alerts || data.alerts.length === 0}
              onExportStart={() => {
                // Log export button click
                if (user) {
                  trackAlertOperation(
                    'Alert List',
                    user.email || user.firstName || 'Unknown',
                    {
                      action: 'Alert List Export Clicked',
                    },
                    {
                      firstName: user.firstName,
                      lastName: user.lastName,
                      role: user.role,
                      roleType: user.roleType,
                      roleLevel: user.roleLevel,
                      userClass: user.class,
                      customerId: user.customerId,
                      customerName: user.customerName,
                      engagementId: user.engagementId,
                      engagementName: user.engagementName,
                      isServiceDeskUser: isServiceDeskUser,
                    }
                  );
                }
              }}
            />
          )}
        </div>
      </div>

      {/* Active Filters Indicator */}
      {activeFilterChips.length > 0 && (
        <div className="mb-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
            onSaveAsDefault={handleSaveFilters}
            isSaving={isFilterSaving}
            hasUnsavedChanges={hasUnsavedFilterChanges}
          />
        </div>
      )}

      {/* Alerts Table */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
          <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {visibleColumns.map((col) => (
                <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader(col.key, col.label)}
                </th>
              ))}
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                View
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data?.alerts?.map((alert: Alert) => {
              const isResolved = alert.state === 'resolved' || alert.state === 'auto_resolved' || alert.state === 'closed';
              const rowClassName = isResolved
                ? "hover:bg-gray-50 bg-gray-25 opacity-75"
                : "hover:bg-gray-50";

              // Column renderer function
              const renderColumn = (columnKey: string) => {
                switch (columnKey) {
                  case 'number':
                    return (
                      <td key={columnKey} className="px-3 py-2">
                        <div>
                          <Link
                            to={`/alerts/${alert.id || alert._id}`}
                            state={{ alertsListSearch: location.search }}
                            className="text-sm font-medium text-indigo-600 hover:text-indigo-900 hover:underline"
                            title="View alert details"
                          >
                            {alert.number}
                          </Link>
                          <div className="text-sm text-gray-500 max-w-xs truncate">{stripHtml(alert.shortDescription)}</div>
                        </div>
                      </td>
                    );
                  case 'shortDescription':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{stripHtml(alert.shortDescription)}</div>
                      </td>
                    );
                  case 'category':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {alert.category ? categoryLabels[alert.category as keyof typeof categoryLabels] || alert.category : '-'}
                      </td>
                    );
                  case 'type':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {alert.type || '-'}
                      </td>
                    );
                  case 'customer':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        {alert.Customer ? (
                          <div>
                            <div className="text-gray-900">{alert.Customer.accountName}</div>
                            {alert.Customer.accountShortName && (
                              <div className="text-gray-500 text-xs">({alert.Customer.accountShortName})</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                    );
                  case 'engagement':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        {alert.Engagement ? (
                          <div>
                            <div className="text-gray-900">{alert.Engagement.name}</div>
                            <div className="text-gray-500 text-xs">{alert.Engagement.status}</div>
                          </div>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                    );
                  case 'state':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stateColors[alert.state as keyof typeof stateColors]}`}>
                            {stateLabels[alert.state as keyof typeof stateLabels] || alert.state}
                          </span>
                          {isResolved && alert.relatedCaseId && (
                            <span className="text-xs text-gray-500" title="Case created from this alert">
                              📋
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  case 'severity':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {alert.severity || '-'}
                      </td>
                    );
                  case 'source':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {alert.source || '-'}
                      </td>
                    );
                  case 'sourceSubService':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {alert.sourceSubService || '-'}
                      </td>
                    );
                  case 'node':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {alert.node || '-'}
                      </td>
                    );
                  case 'resource':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {alert.Resource?.name || (alert as any).resource || '-'}
                      </td>
                    );
                  case 'alertType':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(alert as any).alertType || '-'}
                      </td>
                    );
                  case 'acknowledged':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        {alert.acknowledged ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            No
                          </span>
                        )}
                      </td>
                    );
                  case 'acknowledgedBy':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {alert.acknowledgedBy?.firstName ? `${alert.acknowledgedBy.firstName} ${alert.acknowledgedBy.lastName || ''}` : '-'}
                      </td>
                    );
                  case 'acknowledgedAt':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {alert.acknowledgedAt ? (
                          <div className="flex flex-col">
                            <span>{formatDate(alert.acknowledgedAt)}</span>
                            <span className="text-xs">{formatTime(alert.acknowledgedAt)}</span>
                          </div>
                        ) : '-'}
                      </td>
                    );
                  case 'count':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {alert.count || 1}
                      </td>
                    );
                  case 'firstSeen':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {alert.firstSeen ? (
                          <div className="flex flex-col">
                            <span>{formatDate(alert.firstSeen)}</span>
                            <span className="text-xs">{formatTime(alert.firstSeen)}</span>
                          </div>
                        ) : '-'}
                      </td>
                    );
                  case 'lastSeen':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {alert.lastSeen ? (
                          <div className="flex flex-col">
                            <span>{formatDate(alert.lastSeen)}</span>
                            <span className="text-xs">{formatTime(alert.lastSeen)}</span>
                          </div>
                        ) : '-'}
                      </td>
                    );
                  case 'createdAt':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {alert.createdAt ? (
                          <div className="flex flex-col">
                            <span>{formatDate(alert.createdAt)}</span>
                            <span className="text-xs">{formatTime(alert.createdAt)}</span>
                          </div>
                        ) : '-'}
                      </td>
                    );
                  default:
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        -
                      </td>
                    );
                }
              };

              return (
              <tr key={alert.id || alert._id} className={rowClassName}>
                {visibleColumns.map((col) => renderColumn(col.key))}
                {/* View (Eye Icon) */}
                <td className="px-3 py-2 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <Link
                      to={`/alerts/${alert.id || alert._id}`}
                      state={{ alertsListSearch: location.search }}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    {isResolved && (
                      <LockClosedIcon className="h-4 w-4 text-gray-400" title="Alert is resolved and locked" />
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination Controls - Always visible */}
      {data?.alerts && data.alerts.length > 0 ? (
        <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-4 py-2 rounded-lg shadow flex-shrink-0" style={{ minHeight: '48px' }}>
          {/* Left: Records per page dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                patchSearchParams({
                  pageSize: e.target.value,
                  page: '1',
                });
              }}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {PAGE_SIZE_OPTIONS.map((size, index) => {
                const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                const isDisabled = previousSize >= (data?.total ?? 0) && size > (data?.total ?? 0);
                return (
                  <option key={size} value={size} disabled={isDisabled}>
                    {size}
                  </option>
                );
              })}
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          {/* Center: Entry count */}
          <div className="text-sm text-gray-700">
            Showing {Math.min((page - 1) * pageSize + 1, data?.total ?? 0)} to {Math.min(page * pageSize, data?.total ?? 0)} of {data?.total ?? 0} entries
          </div>

          {/* Right: Pagination buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => patchSearchParams({ page: '1' })}
              disabled={page === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="First page"
            >
              &laquo;
            </button>
            <button
              onClick={() => patchSearchParams({ page: String(page - 1) })}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {/* Page numbers */}
            {(() => {
              const totalPages = Math.ceil((data?.total ?? 0) / pageSize);
              const pages: (number | string)[] = [];

              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                if (page <= 3) {
                  pages.push(1, 2, 3, 4, '...', totalPages);
                } else if (page >= totalPages - 2) {
                  pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                } else {
                  pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                }
              }

              return pages.map((p, idx) => (
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => patchSearchParams({ page: String(p) })}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      page === p
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              ));
            })()}

            <button
              onClick={() => patchSearchParams({ page: String(page + 1) })}
              disabled={page >= Math.ceil((data?.total ?? 0) / pageSize)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() =>
                patchSearchParams({
                  page: String(Math.ceil((data?.total ?? 0) / pageSize)),
                })
              }
              disabled={page >= Math.ceil((data?.total ?? 0) / pageSize)}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last page"
            >
              &raquo;
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex items-center justify-center bg-white px-4 py-8 rounded-lg shadow flex-shrink-0">
          <p className="text-gray-500">No alerts found</p>
        </div>
      )}

      {/* List Layout Settings Modal */}
      <ListLayoutSettingsModal
        isOpen={showLayoutSettings}
        onClose={() => setShowLayoutSettings(false)}
        entityType="ALERT"
        columns={layoutColumns}
        onSave={saveColumns}
        onReset={resetToDefault}
        isSaving={isLayoutSaving}
        isResetting={isLayoutResetting}
      />

    </div>
  );
};

export default AlertList;
